import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { HELP_TOPICS, APP_CONFIG } from '../config/constants';

const HelpSection = styled.section`
  background: transparent;
  min-height: calc(100vh - 80px);
  padding: 4rem 0;
`;

const HelpTitle = styled.h1`
  font-size: 3.5rem;
  margin-bottom: 1.5rem;
  color: rgba(255, 255, 255, 0.95);
  text-align: center;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.1;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HelpSubtitle = styled.p`
  font-size: 1.25rem;
  color: rgba(255, 255, 255, 0.8);
  text-align: center;
  margin-bottom: 4rem;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
  font-weight: 400;
`;

const SearchContainer = styled.div`
  max-width: 700px;
  margin: 0 auto 4rem;
  position: relative;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 1.25rem 3rem 1.25rem 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  font-size: 1.1rem;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  color: rgba(255, 255, 255, 0.95);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-weight: 400;

  &:focus {
    outline: none;
    border-color: rgba(255, 255, 255, 0.4);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.3);
    transform: translateY(-2px);
    background: rgba(255, 255, 255, 0.12);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.6);
    font-weight: 400;
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  right: 1.5rem;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(255, 255, 255, 0.7);
  font-size: 1.2rem;
  pointer-events: none;
  
  &::before {
    content: "🔍";
    font-size: 1.2rem;
  }
`;

const TopicsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2.5rem;
  max-width: 1400px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const TopicCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 2.5rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  position: relative;
  overflow: hidden;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.3);
    border-color: rgba(255, 255, 255, 0.3);
    background: rgba(255, 255, 255, 0.12);
  }
  
  @media (max-width: 768px) {
    padding: 2rem;
  }
`;

const TopicHeader = styled.div`
  margin-bottom: 1rem;
`;

const TopicTitle = styled.h3`
  font-size: 1.6rem;
  color: rgba(255, 255, 255, 0.95);
  margin: 0 0 1rem 0;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.3;
`;

const TopicDescription = styled.p`
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.7;
  margin-bottom: 2rem;
  font-size: 1.05rem;
  font-weight: 400;
`;


const ArticleModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 2rem;
  backdrop-filter: blur(8px);
`;

const ArticleContent = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  padding: 2.5rem;
  max-width: 700px;
  max-height: 85vh;
  overflow-y: auto;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.2);
  position: relative;
  
  @media (max-width: 768px) {
    padding: 2rem;
    margin: 1rem;
    max-height: 90vh;
  }
`;

const ArticleHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
`;

const ArticleTitle = styled.h2`
  font-size: 1.75rem;
  color: rgba(255, 255, 255, 0.95);
  margin: 0;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.3;
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  font-size: 1.5rem;
  color: rgba(255, 255, 255, 0.8);
  cursor: pointer;
  padding: 0.75rem;
  border-radius: 12px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    color: rgba(255, 255, 255, 0.95);
    transform: scale(1.05);
    border-color: rgba(255, 255, 255, 0.3);
  }
`;

const ArticleText = styled.div`
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.8;
  font-size: 1.05rem;
  font-weight: 400;
  
  a {
    color: rgba(255, 255, 255, 0.95);
    text-decoration: none;
    font-weight: 600;
    border-bottom: 1px solid transparent;
    transition: all 0.3s;
    
    &:hover {
      border-bottom-color: rgba(255, 255, 255, 0.95);
    }
  }
`;

const NoResults = styled.div`
  text-align: center;
  padding: 3rem;
  color: rgba(255, 255, 255, 0.7);
  font-size: 1.1rem;
`;

const ArticleItem = styled.div`
  padding: 1.25rem 0;
  margin-bottom: 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    padding-left: 1rem;
    padding-right: 1rem;
    margin-left: -1rem;
    margin-right: -1rem;
    border-radius: 12px;
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const ArticleItemTitle = styled.div`
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  font-size: 1.1rem;
  line-height: 1.4;
  flex: 1;
`;

const ArticleItemPreview = styled.div`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.5;
  margin-top: 0.25rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ContactSection = styled.div`
  max-width: 700px;
  margin: 4rem auto 0;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 2.5rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
  text-align: center;
  
  @media (max-width: 768px) {
    padding: 2rem;
    margin: 3rem auto 0;
  }
`;

const ContactTitle = styled.h3`
  font-size: 1.5rem;
  color: rgba(255, 255, 255, 0.95);
  margin: 0 0 1rem 0;
  font-weight: 700;
  letter-spacing: -0.01em;
`;

const ContactText = styled.p`
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.7;
  font-size: 1.05rem;
  margin-bottom: 1.5rem;
  font-weight: 400;
`;

const ContactEmail = styled.a`
  display: inline-block;
  color: rgba(255, 255, 255, 0.95);
  font-size: 1.1rem;
  font-weight: 600;
  text-decoration: none;
  padding: 0.75rem 1.5rem;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover {
    background: rgba(255, 255, 255, 0.15);
    border-color: rgba(255, 255, 255, 0.3);
    transform: translateY(-2px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
  }
`;


// Component to safely render content with links
const SafeContent = ({ content, isPreview = false }) => {
  const parseContent = (text) => {
    if (isPreview) {
      // For preview, strip HTML tags and truncate
      const stripped = text.replace(/<[^>]*>/g, '');
      return stripped.substring(0, 100) + (stripped.length > 100 ? '...' : '');
    }
    
    // For full content, parse HTML safely
    const parts = [];
    const linkRegex = /<a\s+href="([^"]+)"[^>]*>([^<]+)<\/a>/g;
    let lastIndex = 0;
    let match;
    
    while ((match = linkRegex.exec(text)) !== null) {
      // Add text before the link
      if (match.index > lastIndex) {
        parts.push(text.slice(lastIndex, match.index));
      }
      
      // Add the link as JSX
      parts.push(
        <a 
          key={match.index} 
          href={match[1]} 
          target="_blank" 
          rel="noopener noreferrer"
        >
          {match[2]}
        </a>
      );
      
      lastIndex = match.index + match[0].length;
    }
    
    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }
    
    return parts.length > 0 ? parts : text;
  };
  
  return parseContent(content);
};

const HelpCenter = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedArticle, setSelectedArticle] = useState(null);


  // Filter topics and articles based on search term
  const filteredTopics = useMemo(() => {
    if (!searchTerm.trim()) return HELP_TOPICS;

    const searchLower = searchTerm.toLowerCase();
    
    return HELP_TOPICS.map(topic => {
      const matchingArticles = topic.articles.filter(article => 
        article.title.toLowerCase().includes(searchLower) ||
        article.content.toLowerCase().includes(searchLower)
      );
      
      if (matchingArticles.length > 0) {
        return {
          ...topic,
          articles: matchingArticles
        };
      }
      
      // Also check if topic title or description matches
      if (topic.title.toLowerCase().includes(searchLower) ||
          topic.description.toLowerCase().includes(searchLower)) {
        return topic;
      }
      
      return null;
    }).filter(Boolean);
  }, [searchTerm]);

  const openArticle = (article) => {
    setSelectedArticle(article);
  };

  const closeArticle = () => {
    setSelectedArticle(null);
  };

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && selectedArticle) {
        closeArticle();
      }
    };

    if (selectedArticle) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [selectedArticle]);

  return (
    <HelpSection>
      <div className="container">
        <HelpTitle>Help Center</HelpTitle>
        <HelpSubtitle>
          Find answers to common questions and learn how to get the most out of {APP_CONFIG.name}
        </HelpSubtitle>
        
        <SearchContainer>
          <SearchInput
            type="text"
            placeholder="Search for help articles..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <SearchIcon />
        </SearchContainer>

        {filteredTopics.length === 0 ? (
          <NoResults>
            No articles found for "{searchTerm}". Try a different search term.
          </NoResults>
        ) : (
          <TopicsGrid>
            {filteredTopics.map((topic) => (
              <TopicCard key={topic.id}>
                <TopicHeader>
                  <TopicTitle>{topic.title}</TopicTitle>
                </TopicHeader>
                <TopicDescription>{topic.description}</TopicDescription>
                
                {topic.id === 'setup-automation' && (
                  <div style={{ marginTop: '1.5rem' }}>
                    <Link 
                      to="/setup-automatic-checkins" 
                      style={{
                        display: 'inline-block',
                        background: 'rgba(255, 255, 255, 0.1)',
                        backdropFilter: 'blur(10px)',
                        color: 'rgba(255, 255, 255, 0.95)',
                        padding: '0.75rem 1.25rem',
                        borderRadius: '16px',
                        fontSize: '0.9rem',
                        textDecoration: 'none',
                        fontWeight: '600',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        boxShadow: '0 10px 20px rgba(0, 0, 0, 0.2)'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = 'rgba(255, 255, 255, 0.15)';
                        e.target.style.transform = 'translateY(-1px)';
                        e.target.style.boxShadow = '0 12px 24px rgba(0, 0, 0, 0.3)';
                        e.target.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 10px 20px rgba(0, 0, 0, 0.2)';
                        e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                      }}
                    >
                      Detailed Setup Guide
                    </Link>
                  </div>
                )}
                
                <div style={{ marginTop: '1.5rem' }}>
                  {topic.articles.map((article) => (
                    <ArticleItem
                      key={article.id}
                      onClick={() => openArticle(article)}
                    >
                      <div>
                        <ArticleItemTitle>
                          {article.title}
                        </ArticleItemTitle>
                        <ArticleItemPreview>
                          <SafeContent content={article.content} isPreview={true} />
                        </ArticleItemPreview>
                      </div>
                      <div style={{ 
                        color: 'rgba(255, 255, 255, 0.6)', 
                        fontSize: '1.2rem',
                        marginLeft: '1rem',
                        transition: 'transform 0.3s ease'
                      }}>
                        →
                      </div>
                    </ArticleItem>
                  ))}
                </div>
              </TopicCard>
            ))}
          </TopicsGrid>
        )}

        <ContactSection>
          <ContactTitle>Still need help?</ContactTitle>
          <ContactText>
            If you have questions or need further assistance, feel free to reach out to us.
          </ContactText>
          <ContactEmail href={`mailto:${APP_CONFIG.email}`}>
            {APP_CONFIG.email}
          </ContactEmail>
        </ContactSection>

        {selectedArticle && (
          <ArticleModal onClick={closeArticle}>
            <ArticleContent onClick={(e) => e.stopPropagation()}>
              <ArticleHeader>
                <ArticleTitle>{selectedArticle.title}</ArticleTitle>
                <CloseButton onClick={closeArticle}>×</CloseButton>
              </ArticleHeader>
              <ArticleText>
                <SafeContent content={selectedArticle.content} />
              </ArticleText>
            </ArticleContent>
          </ArticleModal>
        )}
      </div>
    </HelpSection>
  );
};

export default HelpCenter;
